import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { projectComplianceApi, compliancePolicyApi } from '../api/complianceApi';

export const useProjectCompliance = (projectId) => {
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCompliance = useCallback(async () => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await projectComplianceApi.getProjectCompliance(projectId);
      setCompliance(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch compliance data');
      toast.error(err.response?.data?.message || 'Failed to fetch compliance data');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchCompliance();
  }, [fetchCompliance]);

  const toggleCompliance = useCallback(async (itemId) => {
    try {
      const response = await projectComplianceApi.toggleComplianceItem(projectId, itemId);
      setCompliance(response.data);
      toast.success('Compliance status updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update compliance status');
      throw err;
    }
  }, [projectId]);

  const addComplianceItem = useCallback(async (itemData) => {
    try {
      const response = await projectComplianceApi.addComplianceItem(projectId, itemData);
      setCompliance(response.data);
      toast.success('Compliance item added successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add compliance item');
      throw err;
    }
  }, [projectId]);

  const updateComplianceItem = useCallback(async (itemId, itemData) => {
    try {
      const response = await projectComplianceApi.updateComplianceItem(projectId, itemId, itemData);
      setCompliance(response.data);
      toast.success('Compliance item updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update compliance item');
      throw err;
    }
  }, [projectId]);

  const deleteComplianceItem = useCallback(async (itemId) => {
    try {
      const response = await projectComplianceApi.deleteComplianceItem(projectId, itemId);
      setCompliance(response.data);
      toast.success('Compliance item deleted successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete compliance item');
      throw err;
    }
  }, [projectId]);

  const addApproval = useCallback(async (itemId, approvalData) => {
    try {
      const response = await projectComplianceApi.addComplianceApproval(projectId, itemId, approvalData);
      setCompliance(response.data);
      toast.success('Approval added successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add approval');
      throw err;
    }
  }, [projectId]);

  const refreshCompliance = useCallback(async () => {
    try {
      const response = await projectComplianceApi.refreshComplianceItems(projectId);
      setCompliance(response.data);
      toast.success('Compliance items refreshed successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to refresh compliance items');
      throw err;
    }
  }, [projectId]);

  return {
    compliance,
    loading,
    error,
    refetch: fetchCompliance,
    toggleCompliance,
    addComplianceItem,
    updateComplianceItem,
    deleteComplianceItem,
    addApproval,
    refreshCompliance,
  };
};

export const useCompliancePolicies = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPolicies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await compliancePolicyApi.getAllPolicies();
      setPolicies(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch policies');
      toast.error(err.response?.data?.message || 'Failed to fetch policies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const createPolicy = useCallback(async (policyData) => {
    try {
      const response = await compliancePolicyApi.createPolicy(policyData);
      setPolicies(prev => [...prev, response.data]);
      toast.success('Policy created successfully');
      return response.data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create policy');
      throw err;
    }
  }, []);

  const updatePolicy = useCallback(async (id, policyData) => {
    try {
      const response = await compliancePolicyApi.updatePolicy(id, policyData);
      setPolicies(prev => prev.map(p => p._id === id ? response.data : p));
      toast.success('Policy updated successfully');
      return response.data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update policy');
      throw err;
    }
  }, []);

  const deletePolicy = useCallback(async (id) => {
    try {
      await compliancePolicyApi.deletePolicy(id);
      setPolicies(prev => prev.filter(p => p._id !== id));
      toast.success('Policy deleted successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete policy');
      throw err;
    }
  }, []);

  return {
    policies,
    loading,
    error,
    refetch: fetchPolicies,
    createPolicy,
    updatePolicy,
    deletePolicy,
  };
};

export const useComplianceMetrics = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await projectComplianceApi.getComplianceMetrics();
      setMetrics(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch metrics');
      toast.error(err.response?.data?.message || 'Failed to fetch metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return {
    metrics,
    loading,
    error,
    refetch: fetchMetrics,
  };
};

export const useComplianceSummary = () => {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await projectComplianceApi.getComplianceSummary();
      setSummary(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch summary');
      toast.error(err.response?.data?.message || 'Failed to fetch summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return {
    summary,
    loading,
    error,
    refetch: fetchSummary,
  };
};